import {Poi} from '../react-app-env';
import Cache from '../utils/Cache';

/**
 * Service for handling metadata operations.
 */
class MetaDataService {
  private googleMapsApi?: google.maps.Map;
  private googleMapsPlacesApi?: google.maps.places.PlacesService;
  private readonly cache: Cache<PoiMetaData | null>;

  /**
   * Constructs a MetaDataService instance.
   */
  constructor() {
    this.cache = new Cache({
      itemLoader: (placeId) => this.loadGooglePlace(placeId)
    });
  }

  /**
   * Assigns the Google Maps API instance if not already set.
   *
   * @param {google.maps.Map} api The Google Maps API instance to set.
   */
  public setGoogleMapsApi(api: google.maps.Map) {
    if (this.googleMapsApi == null) {
      console.info('MetaDataService Google Maps API set:', api);
    } else if (api !== this.googleMapsApi) {
      console.info('MetaDataService Google Maps API updated:', api);
    }

    this.googleMapsApi = api;
    this.googleMapsPlacesApi = new google.maps.places.PlacesService(api);
  }

  /**
   * Loads Google Place metadata for a given place ID.
   *
   * @param {string} placeId The Google Place ID.
   * @return {Promise<PoiMetaData | null>} The loaded POI metadata or null if not found.
   * @private
   */
  private loadGooglePlace(placeId: string): Promise<PoiMetaData | null> {
    return new Promise((resolve, reject) => {
      try {
        this.getPlacesApi()
            .getDetails({ placeId: placeId }, (result) => {
              if (result == null) {
                const errorMessage = `Empty result for placeId ${placeId}`;
                console.warn(errorMessage);
                reject(new Error(errorMessage));
              } else {
                resolve(this.createMetaData(result));
              }
            });
      } catch(ex) {
        const errorMessage = `Error loading metadata for placeId ${placeId}`;
        console.warn(errorMessage, ex);
        reject(new Error(errorMessage));
      }
    });
  }

  /**
   * Loads metadata for a given Point of Interest (POI).
   *
   * @param {Poi} poi The POI for which to load metadata.
   * @return {Promise<PoiMetaData>} A promise that resolves to the POI metadata.
   */
  public loadMetaData(poi: Poi): Promise<PoiMetaData | null> {
    if (poi.googlePlaceId) {
      return this.cache.get(poi.googlePlaceId);
    } else {
      return Promise.resolve(null);
    }
  }

  /**
   * Maps result from Google Places API to our generic PoiMetaData structure.
   *
   * @param placeResult The Places API result to map
   */
  private createMetaData(placeResult: google.maps.places.PlaceResult | null): PoiMetaData | null {
    if (placeResult == null) {
      return null;
    }

    let thumbnailUrl = undefined;
    if (placeResult.photos) {
      thumbnailUrl = placeResult.photos[0]?.getUrl({ maxWidth: 400, maxHeight: 200 });
    }

    return {
      name:         placeResult.name,
      rating:       placeResult.rating,
      ratingsCount: placeResult.user_ratings_total,
      thumbnailUrl: thumbnailUrl,
      description:  (placeResult as any).editorial_summary?.overview,
      phoneNumber:  placeResult.formatted_phone_number,
      address:      placeResult.formatted_address,
      website:      placeResult.website,
      openingHours: {
        alwaysOpen:        this.isAlwaysOpen(placeResult),
        openNow:           placeResult.opening_hours?.isOpen(),
        permanentlyClosed: placeResult.business_status === google.maps.places.BusinessStatus.CLOSED_PERMANENTLY,
        temporarilyClosed: placeResult.business_status === google.maps.places.BusinessStatus.CLOSED_TEMPORARILY,
        details:           placeResult.opening_hours?.weekday_text
      }
    };
  }

  /**
   * Gets the Google Maps Places API instance.
   */
  private getPlacesApi(): google.maps.places.PlacesService {
    if (this.googleMapsPlacesApi) {
      return this.googleMapsPlacesApi;
    } else {
      throw new Error('Google Maps Places API not set in MetaDataService');
    }
  }

  private isAlwaysOpen(placeResult: google.maps.places.PlaceResult) {
    if (placeResult.opening_hours?.periods?.length !== 1) {
      return false;
    }

    const period = placeResult.opening_hours?.periods?.at(0)?.open;
    return period?.day === 0 && period.time === '0000';
  }
}

/**
 * Interface representing metadata for a Point of Interest (POI).
 */
export interface PoiMetaData {
  name?: string;
  rating?: number;
  ratingsCount?: number;
  thumbnailUrl?: string;
  description?: string;
  phoneNumber?: string;
  address?: string;
  website?: string;
  openingHours: {
    openNow?: boolean;
    alwaysOpen?: boolean;
    permanentlyClosed?: boolean;
    temporarilyClosed?: boolean;
    details?: string[];
  };
}

const metaDataService = new MetaDataService();
export default metaDataService;
